import React from 'react'
import { useSelector } from 'react-redux'
import {
  formatDateToString,
  formatNumber,
  formatCurrency,
} from '../../../helpers'
import { Card } from 'react-bootstrap'
import { CardRowItem } from '../../cardRowItem'
import { IsExGST, MonthlySalaryPackagingDeductions } from './proposalFunctions'
import * as E from './checklistCardElements'
import { rioTintoStaticData } from './rioTintoStaticData'
import {
  ICardRowItem,
  IDatablock,
  IStaticDataItem,
} from './proposalInterfaces/cardStaticDataTypes'
import { ICardChecklistItemProps } from './proposalInterfaces/cardCheckListItemProps'
import { ITheme } from 'application/utils/type'
import { IProposalMonthlySalaryPackagingDeductionsProps } from './proposalInterfaces/deductionSectionProps'

interface IConsultant {
  consultant: { firstName: string; lastName: string }
}

interface IProposalDate {
  proposal: { proposal: { createdOn: string } }
}

interface IEstimateProps
  extends IProposalMonthlySalaryPackagingDeductionsProps {
  annualRate: string
  leaseTerm: string
  kmPerAnnum: string
  vehicle: { invoiceTotal: string }
}

interface IEstimateStateProps {
  individualEstimate: {
    estimate: IEstimateProps
  }
}

interface IEmployerStateProps {
  user: { employer: { employerGRS?: { gstExclusiveDeductionsCode: string } } }
}

const RioTintoFormattedData = (theme: ITheme) => {
  const consultantName = useSelector(
    (state: IConsultant) =>
      state.consultant.firstName + ' ' + state.consultant.lastName
  )
  const proposalDate = useSelector((state: IProposalDate) =>
    formatDateToString(state.proposal.proposal.createdOn, ' ')
  )
  const interestRate = useSelector(
    (state: IEstimateStateProps) => state.individualEstimate.estimate.annualRate
  )

  const term = useSelector(
    (state: IEstimateStateProps) => state.individualEstimate.estimate.leaseTerm
  )

  const annualKm = useSelector(
    (state: IEstimateStateProps) => state.individualEstimate.estimate.kmPerAnnum
  )

  const purchasePrice = useSelector(
    (state: IEstimateStateProps) =>
      state.individualEstimate.estimate.vehicle.invoiceTotal
  )

  const estimate = useSelector(
    (state: IEstimateStateProps) =>
      state.individualEstimate && state.individualEstimate.estimate
  )

  const employer = useSelector(
    (state: IEmployerStateProps) => state.user.employer
  )

  const replaceVariables = (text: string) => {
    var tmp = text
    tmp = tmp.replace('|interestRate|', parseFloat(interestRate).toFixed(2))
    tmp = tmp.replace('|annualKm|', formatNumber(annualKm))
    tmp = tmp.replace('|consultantName|', consultantName)
    tmp = tmp.replace('|proposalDate|', proposalDate ? proposalDate : '')
    tmp = tmp.replace('|purchasePrice|', formatCurrency(purchasePrice))
    tmp = tmp.replace('|term|', term)
    tmp = tmp.replace(
      '|monthlySalaryPackagingDeductions|',
      formatCurrency(
        MonthlySalaryPackagingDeductions(IsExGST(employer), estimate)
      )
    )
    return tmp
  }

  const preparePrefixCard = (data: IStaticDataItem, theme: ITheme) => {
    if (!!data.prefixCard) {
      return (
        <div>
          <div
            dangerouslySetInnerHTML={{
              __html: data.prefix as unknown as string,
            }}
          ></div>
          <Card
            style={{
              borderWidth: 0,
              backgroundColor: '#f7f7f7',
              marginBottom: '12px',
              padding: '24px',
            }}
          >
            {!!data.prefixCard.cardBody && (
              <Card.Body
                dangerouslySetInnerHTML={{ __html: data.prefixCard.cardBody }}
              ></Card.Body>
            )}
            {data.prefixCard.cardRowItems.map((cri: ICardRowItem) => {
              return (
                <CardRowItem
                  title={
                    <E.PrimaryText theme={theme}>{cri.title}</E.PrimaryText>
                  }
                  value={replaceVariables(cri.value)}
                  isCurrency={!!cri.isCurrency}
                  longVal={!!cri.longVal}
                  rightTextcss={!!cri.boldVal ? 'font-weight-bold' : ''}
                />
              )
            })}
          </Card>
        </div>
      )
    }
  }

  const prepareSuffixCard = (data: IStaticDataItem, theme: ITheme) => {
    if (!!data.suffixCard)
      return (
        <div>
          <Card
            style={{
              borderWidth: '0',
              backgroundColor: '#f7f7f7',
            }}
          >
            <Card.Body
              dangerouslySetInnerHTML={{ __html: data.suffixCard.cardBody }}
            ></Card.Body>
            {data.suffixCard.cardRowItems.map((cri: ICardRowItem) => {
              return (
                <CardRowItem
                  title={
                    <E.PrimaryText theme={theme}>{cri.title}</E.PrimaryText>
                  }
                  value={replaceVariables(cri.value)}
                  isCurrency={!!cri.isCurrency}
                  longVal={!!cri.longVal}
                  rightTextcss="font-weight-bold"
                />
              )
            })}
          </Card>
        </div>
      )
  }

  return rioTintoStaticData.map((card: any) => {
    return {
      cardNumber: card.cardNumber,
      cardHeadingNumber: card.cardHeadingNumber,
      title: card.title,
      hideInPdf: card.hideInPdf,
      prefix: preparePrefixCard(card, theme),
      prefixPdf: card.prefixPdf,
      suffix: prepareSuffixCard(card, theme),
      pdfText: card.pdfText,
      datablocks: card.datablocks.map((db: IDatablock) => {
        return {
          heading: db.heading,
          showIds: db.showIds,
          checklistItems: db.checklistItems.map(
            (ci: ICardChecklistItemProps) => {
              return {
                id: ci.id,
                isChecked: ci.isChecked,
                text: replaceVariables(ci.text),
              }
            }
          ),
        }
      }),
    }
  })
}

export default RioTintoFormattedData
